import { useQuery, UseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import {
    FilterOptionsOrganization,
    useFilterOrganizations as useFilterOrganizationsClient,
    useGetOrganizationById,
} from './Organization';
import { getStaleTimeInMinutes } from '@/shared/utils/staleTime.ts';

const STALE_TIME = getStaleTimeInMinutes(5);

export function getUseOrganizationByIdQueryKey(organizationId: number) {
    return ['organization', organizationId];
}

export function useGetOrganizationQueryOptions() {
    const { getOrganizationById } = useGetOrganizationById();
    return (id: number) =>
        ({
            queryKey: getUseOrganizationByIdQueryKey(id),
            queryFn: () => getOrganizationById(id),
            staleTime: STALE_TIME,
        }) satisfies UseQueryOptions;
}

export function useOrganization(id: number | undefined | null) {
    const getOrganizationQueryOptions = useGetOrganizationQueryOptions();

    return useQuery({
        ...getOrganizationQueryOptions(id!),
        enabled: !!id,
    });
}

/**
 * @deprecated The hook should not be used, please use useOrganizationById instead.
 */
export function useOrganizationSuspenseQuery(id: number | undefined | null) {
    const { getOrganizationById } = useGetOrganizationById();

    return useSuspenseQuery({
        queryKey: ['organization', id],
        queryFn: () => (id ? getOrganizationById(id) : null),
        staleTime: STALE_TIME,
    });
}

export function useOrganizationById(id: number) {
    const { getOrganizationById } = useGetOrganizationById();

    return useSuspenseQuery({
        queryKey: getUseOrganizationByIdQueryKey(id),
        queryFn: () => getOrganizationById(id),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
    });
}

export function useFilterOrganizations(filterOptions: FilterOptionsOrganization, enabled = true) {
    const { filterOrganizations } = useFilterOrganizationsClient();

    return useQuery({
        queryKey: ['organizations', filterOptions],
        queryFn: () => filterOrganizations(filterOptions),
        enabled,
        staleTime: STALE_TIME,
    });
}

export function useOrganizationName(id: number | undefined | null) {
    const { data } = useOrganization(id);

    return data?.name;
}
